import { useState } from 'react';
import { BookOpen, Building2, Check, ShieldCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { showSuccessToast } from '@/components/NotificationToast';
import { decisions, type Decision, type DecisionStatus } from './decisions';

const statusChip: Record<Exclude<DecisionStatus, 'pending'>, { label: string; className: string }> = {
  approved: { label: 'Одобрено', className: 'bg-green-50 text-green-700' },
  rejected: { label: 'Отхвърлено', className: 'bg-red-50 text-red-600' },
};

export const DecisionsPage = (): JSX.Element => {
  const [statuses, setStatuses] = useState<Record<string, DecisionStatus>>(() =>
    Object.fromEntries(decisions.map((d) => [d.id, d.status]))
  );

  const resolve = (decision: Decision, status: 'approved' | 'rejected') => {
    setStatuses((prev) => ({ ...prev, [decision.id]: status }));
    showSuccessToast(
      status === 'approved' ? `„${decision.title}“ — одобрено (демо)` : `„${decision.title}“ — отхвърлено (демо)`
    );
  };

  const pending = decisions.filter((d) => statuses[d.id] === 'pending');
  const resolved = decisions.filter((d) => statuses[d.id] !== 'pending');

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center gap-2.5">
        <ShieldCheck className="h-6 w-6 text-[#0000FF]" />
        <h1 className="text-xl font-bold text-neutral-900">Решения</h1>
        {pending.length > 0 && (
          <span className="rounded-full bg-[#0000FF] px-2 py-0.5 text-xs font-medium text-white">
            {pending.length}
          </span>
        )}
      </div>

      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">
        Чакащи одобрение
      </h2>
      {pending.length === 0 ? (
        <p className="mb-8 rounded-xl border border-dashed border-neutral-200 px-4 py-6 text-center text-sm text-neutral-400">
          Няма чакащи решения.
        </p>
      ) : (
        <div className="mb-8 space-y-3">
          {pending.map((decision) => (
            <div key={decision.id} className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-500">
                  {decision.type}
                </span>
                {decision.client && (
                  <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                    <Building2 className="h-3 w-3 text-neutral-400" />
                    {decision.client}
                  </span>
                )}
                <span className="ml-auto text-xs text-neutral-400">
                  {decision.proposedBy} · {decision.date}
                </span>
              </div>

              <h3 className="mt-2 text-sm font-semibold text-neutral-900">{decision.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-neutral-600">{decision.summary}</p>
              {decision.impact && (
                <p className="mt-1.5 text-xs text-neutral-500">
                  <span className="font-medium text-neutral-600">При одобрение:</span> {decision.impact}
                </p>
              )}

              {decision.evidence && decision.evidence.length > 0 && (
                <div className="mt-2.5 space-y-1 border-t border-neutral-100 pt-2">
                  {decision.evidence.map((item, i) => (
                    <p key={i} className="flex items-start gap-1.5 text-xs text-neutral-500">
                      <BookOpen className="mt-0.5 h-3 w-3 shrink-0 text-neutral-400" />
                      <span>
                        <span className="font-medium text-neutral-600">{item.label}</span> — {item.detail}
                      </span>
                    </p>
                  ))}
                </div>
              )}

              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="blue" onClick={() => resolve(decision, 'approved')}>
                  <Check className="mr-1 h-3.5 w-3.5" />
                  Одобри
                </Button>
                <Button size="sm" variant="outlineAccent" onClick={() => resolve(decision, 'rejected')}>
                  <X className="mr-1 h-3.5 w-3.5" />
                  Отхвърли
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">История</h2>
      <div className="space-y-1.5">
        {resolved.map((decision) => {
          const status = statuses[decision.id] as Exclude<DecisionStatus, 'pending'>;
          return (
            <div
              key={decision.id}
              className="flex items-start gap-3 rounded-lg border border-neutral-100 bg-white px-3 py-2.5"
            >
              <span
                className={cn(
                  'mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium',
                  statusChip[status].className
                )}
              >
                {statusChip[status].label}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-neutral-800">{decision.title}</p>
                <p className="text-xs text-neutral-400">
                  {decision.client ? `${decision.client} · ` : ''}
                  {decision.resolvedBy ?? `днес · ${decision.proposedBy}`}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
